import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import BpmnEditor from '../components/BpmnEditor';
import AIProcessOptimizer from '../components/AIProcessOptimizer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, 
  Edit, 
  Copy, 
  Trash2, 
  CheckCircle,
  Loader2,
  Calendar,
  User,
  Building,
  Tag,
  Link2,
  Lightbulb,
  FolderKanban,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const ProcessMapDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, isAdmin, isApprover } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [processMap, setProcessMap] = useState(null);
  const [linkedIdeas, setLinkedIdeas] = useState([]);
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [showAIOptimizer, setShowAIOptimizer] = useState(false);

  useEffect(() => {
    fetchProcessMap();
  }, [id]);

  const fetchProcessMap = async () => {
    try {
      const response = await api.get(`/process-maps/${id}`);
      setProcessMap(response.data);
      
      // Fetch linked ideas and projects
      if (response.data.linked_ideas?.length > 0) {
        const ideas = await Promise.all(
          response.data.linked_ideas.map(ideaId => 
            api.get(`/ideas/${ideaId}`).then(r => r.data).catch(() => null)
          )
        );
        setLinkedIdeas(ideas.filter(Boolean));
      }
      
      if (response.data.linked_projects?.length > 0) {
        const projects = await Promise.all(
          response.data.linked_projects.map(projId => 
            api.get(`/projects/${projId}`).then(r => r.data).catch(() => null)
          )
        );
        setLinkedProjects(projects.filter(Boolean));
      }
    } catch (error) {
      console.error('Failed to fetch process map:', error);
      toast.error('Failed to load process map');
      navigate('/process-maps');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this process map?')) return;
    
    try {
      await api.delete(`/process-maps/${id}`);
      toast.success('Process map deleted');
      navigate('/process-maps');
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete process map');
    }
  };

  const handleDuplicate = async () => {
    try {
      const response = await api.post(`/process-maps/${id}/duplicate`);
      toast.success('Process map duplicated');
      navigate(`/process-maps/${response.data.id}/edit`);
    } catch (error) {
      console.error('Failed to duplicate:', error);
      toast.error('Failed to duplicate process map');
    }
  };

  const handlePublish = async () => {
    try {
      await api.post(`/process-maps/${id}/publish`);
      toast.success('Process map published');
      setProcessMap(prev => ({ ...prev, status: 'Published' }));
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error('Failed to publish process map');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Published': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Draft': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'Archived': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  if (!processMap) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Process map not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="process-map-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
                {processMap.name}
              </h1>
              <Badge className={getStatusColor(processMap.status)}>
                {processMap.status}
              </Badge>
            </div>
            {processMap.description && (
              <p className="text-slate-500 mt-1">{processMap.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button 
            variant="outline" 
            onClick={() => setShowAIOptimizer(!showAIOptimizer)}
            className="border-[#26B8A7] text-[#26B8A7] hover:bg-[#26B8A7]/10"
            data-testid="ai-optimize-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Optimize
          </Button>
          {processMap.status === 'Draft' && (isApprover() || isAdmin()) && (
            <Button variant="outline" onClick={handlePublish}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Publish
            </Button>
          )}
          <Button variant="outline" onClick={handleDuplicate}>
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate(`/process-maps/${id}/edit`)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
          {(isAdmin() || processMap.created_by === user?.id) && (
            <Button 
              variant="outline" 
              onClick={handleDelete}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* AI Process Optimizer */}
      {showAIOptimizer && (
        <AIProcessOptimizer 
          mapId={id} 
          mapName={processMap.name}
          onClose={() => setShowAIOptimizer(false)}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Info Panel */}
        <div className="lg:col-span-1 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider">
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <Building className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Department</p>
                  <p className="font-medium">{processMap.department || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Tag className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Process Area</p>
                  <p className="font-medium">{processMap.process_area || '-'}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Created By</p>
                  <p className="font-medium">{processMap.created_by_name}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Last Updated</p>
                  <p className="font-medium">
                    {new Date(processMap.updated_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-1">Version</p>
                <Badge variant="outline">v{processMap.version}</Badge>
              </div>
              
              {processMap.tags?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {processMap.tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked Items */}
          {(linkedIdeas.length > 0 || linkedProjects.length > 0) && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Linked Items
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {linkedIdeas.map(idea => (
                  <div 
                    key={idea.id}
                    className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 cursor-pointer hover:bg-slate-100"
                    onClick={() => navigate(`/ideas/${idea.id}`)}
                  >
                    <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">{idea.title}</p>
                      <p className="text-xs text-slate-500">{idea.status}</p>
                    </div>
                  </div>
                ))}
                {linkedProjects.map(project => (
                  <div 
                    key={project.id}
                    className="flex items-start gap-2 p-2 rounded-lg bg-slate-50 cursor-pointer hover:bg-slate-100"
                    onClick={() => navigate(`/projects/${project.id}`)}
                  >
                    <FolderKanban className="w-4 h-4 text-blue-500 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">{project.project_name}</p>
                      <p className="text-xs text-slate-500">{project.current_status}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* BPMN Viewer */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-0">
            <CardTitle className="text-lg">Process Diagram</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="border-t">
              <BpmnEditor
                xml={processMap.xml_content}
                readOnly={true}
                height={600}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProcessMapDetail;
